import { AddItemSlotsRequest } from "../../../../../apis/requests/AddItemSlotsRequest";
import { UpdateIdResponse } from "../../../../../apis/responses/UpdateIdResponse";
import { CharacterHelpers, CharacterInitializer, CharacterItemCollectionName } from "../../../../../entities/characters/Character";
import { ItemHelpers } from "../../../../../entities/library/items/Item";
import { ItemInitializer, ModInitializer } from "../../../../../entities/library/items/ItemInitializers";
import { ModSlot } from "../../../../../entities/library/items/mods/ModSlot";
import { OwnedItemReference, OwnedModReference } from "../../../../../entities/Ownership";
import { ErrorToast, InfoToast, SuccessToast } from "../../../../../entities/toasts/Toasts";
import { getUniqueIdentifier } from "../../../../../utils/GUID";
import { AppThunkAction } from "../../../ApplicationState";
import { CharacterUpdated } from "../CharacterStore.Actions";
import { ToastAction, ToastDispatchables } from "../../../toasts/Toasts.Actions";
import { SiteSetLoading } from "../../../site/SiteStore.Actions";
import { ApiDefaultState } from "../../../api/ApiStore.State";

export type MoveItemGroupOperation = { group: OwnedItemReference[], collection: CharacterItemCollectionName };

export type CharacterSaveItem = { type: 'CHARACTER_SAVE_ITEM', characterId: string, oldInstanceId: string, newInstanceId: string }
export type CharacterAddItem = { type: 'CHARACTER_ADD_ITEM', characterId: string, item: OwnedItemReference }
export type CharacterUpdateItemName = { type: 'CHARACTER_UPDATE_ITEM_NAME', characterId: string, itemInstanceId: string, customName?: string }
export type CharacterUpdateItemNotes = { type: 'CHARACTER_UPDATE_ITEM_NOTES', characterId: string, itemInstanceId: string, customNotes?: string }
export type CharacterRemoveItems = { type: 'CHARACTER_REMOVE_ITEMS', characterId: string, instanceIds: string[] }
export type CharacterMoveItems = { type: 'CHARACTER_MOVE_ITEMS', characterId: string, operationOne: MoveItemGroupOperation, operationTwo?: MoveItemGroupOperation };
export type CharacterUpdateItems = { type: 'CHARACTER_UPDATE_ITEMS', characterId: string, items: OwnedItemReference[] }

export type CharacterAssignMod = { type: 'CHARACTER_ASSIGN_MOD', characterId: string, itemInstanceId: string, modInstanceId: string, slotId: string }
export type CharacterAssignBodyMod = { type: 'CHARACTER_ASSIGN_BODY_MOD', characterId: string, modInstanceId: string, slotId: string }
export type CharacterRemoveMod = { type: 'CHARACTER_REMOVE_MOD', characterId: string, itemInstanceId: string, slotId: string }
export type CharacterRemoveBodyMod = { type: 'CHARACTER_REMOVE_BODY_MOD', characterId: string, slotId: string }
export type CharacterAddSlots = { type: 'CHARACTER_ADD_SLOTS', characterId: string, requests: AddItemSlotsRequest[] }
export type CharacterRemoveSlots = { type: 'CHARACTER_REMOVE_SLOTS', characterId: string, itemInstanceIds: string[], slotIds: string[] }
export type CharacterUpdateSlots = { type: 'CHARACTER_UPDATE_SLOTS', characterId: string, itemInstanceIds: string[], updateIdResponses: UpdateIdResponse[] }

export type CharacterItemAction = CharacterSaveItem
    | CharacterAddItem
    | CharacterUpdateItemName
    | CharacterUpdateItemNotes
    | CharacterMoveItems
    | CharacterRemoveItems
    | CharacterUpdateItems

    | CharacterAssignMod
    | CharacterAssignBodyMod
    | CharacterRemoveMod
    | CharacterRemoveBodyMod
    | CharacterAddSlots
    | CharacterRemoveSlots
    | CharacterUpdateSlots;

export const CharacterItemActions = {
    addItem: (character: CharacterInitializer, item: ItemInitializer): AppThunkAction<CharacterItemAction | ToastAction> =>
        async (dispatch, getState) => {
            const instanceId = getUniqueIdentifier();
            const placeholder = { ...item, instanceId, customSlots: [], mods: [], saved: false };

            const undo = () => {
                dispatch({ type: 'CHARACTER_REMOVE_ITEMS', characterId: character.id, instanceIds: [instanceId] });
                ToastDispatchables.toastLegacy(new ErrorToast(`Error adding ${item.name}.`), dispatch);
            }
            dispatch({ type: 'CHARACTER_ADD_ITEM', characterId: character.id, item: placeholder });

            try {
                const response = await ApiDefaultState.characters.addItem(character.id, item.id);
                if (response.status == 'Success') {
                    dispatch({ type: 'CHARACTER_SAVE_ITEM', characterId: character.id, oldInstanceId: instanceId, newInstanceId: response.payload });
                    ToastDispatchables.toastLegacy(new InfoToast(`${item.name} added to inventory.`), dispatch);
                }
                else {
                    undo();
                    ToastDispatchables.toastValidationResults(response.validationResults, dispatch);
                }
            }
            catch (e) {
                undo();
                ToastDispatchables.toastLegacy(new ErrorToast("Server error adding item."), dispatch);
                console.error(e);
            }
        },

    addItemRange: (character: CharacterInitializer, items: ItemInitializer[], successCallback: (id: string) => unknown): AppThunkAction<CharacterUpdated | ToastAction> =>
        async (dispatch, getState) => {

            try {
                const response = await ApiDefaultState.characters.addItemRange(character.id, items.map(i => i.id));
                if (response.status == 'Error') {
                    ToastDispatchables.toastLegacy(new ErrorToast(`Error adding items to ${character.name}.`), dispatch);
                    return;
                }
                if (response.payload != null) {
                    dispatch({ type: 'CHARACTER_UPDATED', character: response.payload });
                }
                ToastDispatchables.toastLegacy(new SuccessToast("Items added."), dispatch);
                successCallback(character.id);
            }
            catch (e) {
                ToastDispatchables.toastLegacy(new ErrorToast("Server error adding items."), dispatch);
            }
        },

    updateCustomItemName: (character: CharacterInitializer, reference: OwnedItemReference, newCustomName: string | undefined): AppThunkAction<CharacterItemAction | ToastAction> =>
        async (dispatch, getState) => {
            const oldCustomName = reference.customName;
            const oldVersion = { ...reference };
            const newVersion = { ...reference, customName: newCustomName };
            if (newCustomName == undefined || newCustomName.trim() == '') {
                delete newVersion.customName;
            }

            const item = getState().library.items.find(i => i.id == reference.id);
            if (item == null) {
                throw "Attempted to update custom name for nonexistent item.";
            }

            const itemName = reference.customName ?? item.name;

            const undo = () => {
                dispatch({ type: 'CHARACTER_UPDATE_ITEM_NAME', characterId: character.id, itemInstanceId: reference.instanceId, customName: oldCustomName });
                ToastDispatchables.toastLegacy(new ErrorToast(`Error updating ${itemName}.`), dispatch);
            }
            dispatch({ type: 'CHARACTER_UPDATE_ITEM_NAME', characterId: character.id, itemInstanceId: reference.instanceId, customName: newVersion.customName });

            try {
                const response = await ApiDefaultState.characters.updateItem(character.id, oldVersion, newVersion);
                if (response.status == 'Success') {
                    dispatch({ type: 'CHARACTER_SAVE_ITEM', characterId: character.id, oldInstanceId: reference.instanceId, newInstanceId: reference.instanceId });
                }
                else {
                    undo();
                    ToastDispatchables.toastValidationResults(response.validationResults, dispatch);
                }
            }
            catch (e) {
                undo();
                ToastDispatchables.toastLegacy(new ErrorToast("Server error updating item name."), dispatch);
                console.error(e);
            }
        },

    updateCustomItemNotes: (character: CharacterInitializer, reference: OwnedItemReference, newCustomNotes: string | undefined): AppThunkAction<CharacterItemAction | ToastAction> =>
        async (dispatch, getState) => {
            const oldCustomNotes = reference.customNotes;
            const update = { ...reference, customNotes: newCustomNotes };

            const item = getState().library.items.find(i => i.id == reference.id);
            if (item == null) {
                throw "Attempted to update custom name for nonexistent item.";
            }

            const itemName = reference.customName ?? item.name;

            const undo = () => {
                dispatch({ type: 'CHARACTER_UPDATE_ITEM_NOTES', characterId: character.id, itemInstanceId: reference.instanceId, customNotes: oldCustomNotes });
                ToastDispatchables.toastLegacy(new ErrorToast(`Error updating ${itemName}.`), dispatch);
            }
            dispatch({ type: 'CHARACTER_UPDATE_ITEM_NOTES', characterId: character.id, itemInstanceId: reference.instanceId, customNotes: newCustomNotes });

            try {
                const response = await ApiDefaultState.characters.updateItem(character.id, reference, update);
                if (response.status == 'Error') {
                    undo();
                    ToastDispatchables.toastValidationResults(response.validationResults, dispatch);
                }
            }
            catch (e) {
                undo();
                ToastDispatchables.toastLegacy(new ErrorToast("Server error updating item notes."), dispatch);
                console.error(e);
            }
        },

    holdItem: (character: CharacterInitializer, reference: OwnedItemReference, replaceItems: OwnedItemReference[] = []): AppThunkAction<CharacterMoveItems | ToastAction> =>
        async (dispatch, getState) => {
            const undo = getMoveItemUndoFunction(character, reference, replaceItems, dispatch);
            if (undo == undefined) {
                return;
            }

            let toDispatch: CharacterMoveItems = { type: 'CHARACTER_MOVE_ITEMS', characterId: character.id, operationOne: { group: [reference], collection: 'held' } };
            if (replaceItems.any()) {
                toDispatch = { ...toDispatch, operationTwo: { group: replaceItems, collection: 'loose' } };
            }
            dispatch(toDispatch);

            try {
                const replaceInstanceIds = replaceItems.map(ri => ri.instanceId);
                const response = await ApiDefaultState.characters.holdItem(character.id, reference.instanceId, replaceInstanceIds);
                if (response.status == 'Error') {
                    undo();
                    ToastDispatchables.toastValidationResults(response.validationResults, dispatch);
                }
            }
            catch (e) {
                undo();
                ToastDispatchables.toastLegacy(new ErrorToast("Server error holding item."), dispatch);
                console.error(e);
            }
        },

    wearItem: (character: CharacterInitializer, reference: OwnedItemReference, replaceItems: OwnedItemReference[] = []): AppThunkAction<CharacterMoveItems | ToastAction> =>
        async (dispatch, getState) => {
            const undo = getMoveItemUndoFunction(character, reference, replaceItems, dispatch);
            if (undo == undefined) {
                return;
            }

            let toDispatch: CharacterMoveItems = { type: 'CHARACTER_MOVE_ITEMS', characterId: character.id, operationOne: { group: [reference], collection: 'worn' } };
            if (replaceItems.any()) {
                toDispatch = { ...toDispatch, operationTwo: { group: replaceItems, collection: 'loose' } };
            }
            dispatch(toDispatch);
            try {
                const replaceInstanceIds = replaceItems.map(ri => ri.instanceId);
                const response = await ApiDefaultState.characters.wearItem(character.id, reference.instanceId, replaceInstanceIds);
                if (response.status == 'Error') {
                    undo();
                    ToastDispatchables.toastValidationResults(response.validationResults, dispatch);
                }
            }
            catch (e) {
                undo();
                ToastDispatchables.toastLegacy(new ErrorToast("Server error wearing item."), dispatch);
                console.error(e);
            }
        },

    moveToLoose: (character: CharacterInitializer, reference: OwnedItemReference): AppThunkAction<CharacterMoveItems | ToastAction> =>
        async (dispatch, getState) => {
            const undo = getMoveItemUndoFunction(character, reference, [], dispatch);
            if (undo == undefined) {
                return;
            }

            dispatch({ type: 'CHARACTER_MOVE_ITEMS', characterId: character.id, operationOne: { group: [reference], collection: 'loose' } });
            try {
                const response = await ApiDefaultState.characters.moveToLoose(character.id, reference.instanceId);
                if (response.status == 'Error') {
                    undo();
                    ToastDispatchables.toastValidationResults(response.validationResults, dispatch);
                }
            }
            catch (e) {
                undo();
                ToastDispatchables.toastLegacy(new ErrorToast("Server error moving item."), dispatch);
                console.error(e);
            }
        },

    removeItem: (character: CharacterInitializer, reference: OwnedItemReference): AppThunkAction<CharacterItemAction | ToastAction> =>
        async (dispatch, getState) => {
            const item = getState().library.items.find(i => i.id == reference.id);
            if (item == null) {
                ToastDispatchables.toastLegacy(new ErrorToast("No backing item found."), dispatch);
                return;
            }

            const itemName = ItemHelpers.getName(item, reference);

            const undo = () => {
                dispatch({ type: 'CHARACTER_ADD_ITEM', characterId: character.id, item: reference });
                ToastDispatchables.toastLegacy(new ErrorToast(`Error removing ${itemName}.`), dispatch);
            }
            dispatch({ type: 'CHARACTER_REMOVE_ITEMS', characterId: character.id, instanceIds: [reference.instanceId] });

            try {
                const response = await ApiDefaultState.characters.removeItem(character.id, reference.instanceId);
                if (response.status == 'Success') {
                    ToastDispatchables.toastLegacy(new InfoToast(`${itemName} removed from inventory.`), dispatch);
                }
                else {
                    undo();
                    ToastDispatchables.toastValidationResults(response.validationResults, dispatch);
                }
            }
            catch (e) {
                undo();
                ToastDispatchables.toastLegacy(new ErrorToast("Server error removing item."), dispatch);
                console.error(e);
            }
        },

    assignMod: (character: CharacterInitializer, itemInstanceId: string, modInstanceId: string, slotId: string): AppThunkAction<CharacterItemAction | ToastAction> =>
        async (dispatch, getState) => {
            const itemResult = CharacterHelpers.searchItemByInstanceId(character, itemInstanceId);
            if (itemResult == null) {
                ToastDispatchables.toastLegacy(new ErrorToast("Attempt to assign mod to nonexistent item."), dispatch);
                return;
            }

            const items = getState().library.items;

            const slottingItemReference = CharacterHelpers.findSlottingItem(character, items, slotId);
            if (slottingItemReference == null) {
                ToastDispatchables.toastLegacy(new ErrorToast("No item found with that slot."), dispatch);
                return;
            }

            const slottingItem = items.find(i => i.id == slottingItemReference.id);
            if (slottingItem == null) {
                ToastDispatchables.toastLegacy(new ErrorToast("No backing item found."), dispatch);
                return;
            }

            const modResult = CharacterHelpers.searchItemByInstanceId(character, modInstanceId);
            if (modResult == null) {
                ToastDispatchables.toastLegacy(new ErrorToast("Mod not found."), dispatch);
                return;
            }
            const { reference: modReference } = modResult;

            const mod = items.find(i => i.id == modReference.id);
            if (mod == null) {
                ToastDispatchables.toastLegacy(new ErrorToast("No backing mod found."), dispatch);
                return;
            }

            const undo = () => {
                dispatch({ type: 'CHARACTER_REMOVE_MOD', characterId: character.id, itemInstanceId: slottingItemReference.instanceId, slotId });
                ToastDispatchables.toastLegacy(new ErrorToast(`Error assigning ${mod.name} to ${ItemHelpers.getName(slottingItem, slottingItemReference)}.`), dispatch);
            }
            dispatch({ type: 'CHARACTER_ASSIGN_MOD', characterId: character.id, itemInstanceId: slottingItemReference.instanceId, modInstanceId: modReference.instanceId, slotId });
            const updatedMod: OwnedModReference = { ...modReference, assignedSlotId: slotId };
    
            try {
                const response = await ApiDefaultState.characters.assignMod(character.id, slottingItemReference.instanceId, updatedMod);
                if (response.status == 'Error') {
                    undo();
                    ToastDispatchables.toastValidationResults(response.validationResults, dispatch);
                }
            }
            catch (e) {
                undo();
                ToastDispatchables.toastLegacy(new ErrorToast("Server error assigning mod to item."), dispatch);
                console.error(e);
            }
        },

    assignBodyMod: (character: CharacterInitializer, modReference: OwnedModReference, slotId: string): AppThunkAction<CharacterItemAction | ToastAction> =>
        async (dispatch, getState) => {
            const mod = getState().library.items.find(i => i.id == modReference.id);
            if (mod == null) {
                ToastDispatchables.toastLegacy(new ErrorToast("No backing mod found."), dispatch);
                return;
            }

            const undo = () => {
                dispatch({ type: 'CHARACTER_REMOVE_BODY_MOD', characterId: character.id, slotId });
                ToastDispatchables.toastLegacy(new ErrorToast(`Error assigning ${ItemHelpers.getName(mod, modReference)} to body.`), dispatch);
            }
            dispatch({ type: 'CHARACTER_ASSIGN_BODY_MOD', characterId: character.id, modInstanceId: modReference.instanceId, slotId });
            const updatedMod = { ...modReference, assignedSlotId: slotId };

            try {
                const response = await ApiDefaultState.characters.assignBodyMod(character.id, updatedMod);
                if (response.status == 'Error') {
                    undo();
                    ToastDispatchables.toastValidationResults(response.validationResults, dispatch);
                }
            }
            catch (e) {
                undo();
                ToastDispatchables.toastLegacy(new ErrorToast("Server error assigning body mod."), dispatch);
                console.error(e);
            }
        },

    addAndAssignMod: (character: CharacterInitializer, reference: OwnedItemReference, modInitializer: ModInitializer, slotId: string): AppThunkAction<CharacterItemAction | ToastAction | SiteSetLoading> =>
        async (dispatch, getState) => {
            const allItems = getState().library.items;

            const slottingItem = allItems.find(i => i.id == reference.id);
            if (slottingItem == null) {
                ToastDispatchables.toastLegacy(new ErrorToast("No backing item found."), dispatch);
                return;
            }

            const mod: OwnedModReference = {
                id: modInitializer.id,
                instanceId: getUniqueIdentifier(),
                type: modInitializer.type,
                assignedSlotId: slotId,
                assignedItemId: reference.instanceId,
                customSlots: [],
                customName: undefined,
                customNotes: undefined,
            };
            const slottingItemReference = CharacterHelpers.findSlottingItem(character, allItems, slotId);
            if (slottingItemReference == null) {
                ToastDispatchables.toastLegacy(new ErrorToast("No item found with that slot."), dispatch);
                return;
            }

            const undo = () => {
                dispatch({ type: 'CHARACTER_REMOVE_MOD', characterId: character.id, itemInstanceId: slottingItemReference.instanceId, slotId });
                dispatch({ type: 'CHARACTER_REMOVE_ITEMS', characterId: character.id, instanceIds: [mod.instanceId] });
                ToastDispatchables.toastLegacy(new ErrorToast(`Error adding and assigning ${modInitializer.name} to ${ItemHelpers.getName(slottingItem, slottingItemReference)}.`), dispatch);
            }

            dispatch({ type: 'CHARACTER_ADD_ITEM', characterId: character.id, item: mod });
            dispatch({ type: 'CHARACTER_ASSIGN_MOD', characterId: character.id, itemInstanceId: slottingItemReference.instanceId, modInstanceId: mod.instanceId, slotId });
            try {
                const assignModResponse = await ApiDefaultState.characters.addAndAssignMod(character.id, slottingItemReference.instanceId, mod);
                if (assignModResponse.status == 'Error') {
                    ToastDispatchables.toastLegacy(new ErrorToast("Error assigning mod."), dispatch);
                    undo();
                }
                else {
                    dispatch({ type: 'CHARACTER_SAVE_ITEM', characterId: character.id, oldInstanceId: mod.instanceId, newInstanceId: assignModResponse.payload });
                }
            }
            catch(e) {
                undo();
                ToastDispatchables.toastLegacy(new ErrorToast("Server error adding and assigning mod to item."), dispatch);
                console.error(e);
            }
        },

    addAndAssignBodyMod: (character: CharacterInitializer, modInitializer: ModInitializer, slotId: string): AppThunkAction<CharacterItemAction | ToastAction | SiteSetLoading> => 
        async (dispatch, getState) => {
            const mod: OwnedModReference = {
                id: modInitializer.id,
                instanceId: getUniqueIdentifier(),
                type: modInitializer.type,
                assignedSlotId: slotId,
                assignedItemId: undefined,
                customSlots: [],
                customName: undefined,
                customNotes: undefined,
            };
            const undo = () => {
                dispatch({ type: 'CHARACTER_REMOVE_BODY_MOD', characterId: character.id, slotId });
                dispatch({ type: 'CHARACTER_REMOVE_ITEMS', characterId: character.id, instanceIds: [mod.instanceId] });
            }

            dispatch({ type: 'CHARACTER_ADD_ITEM', characterId: character.id, item: mod });
            dispatch({ type: 'CHARACTER_ASSIGN_BODY_MOD', characterId: character.id, modInstanceId: mod.instanceId, slotId });

            try {
                const assignModResponse = await ApiDefaultState.characters.addAndAssignBodyMod(character.id, mod);
                if (assignModResponse.status == 'Error') {
                    ToastDispatchables.toastLegacy(new ErrorToast("Error assigning body mod."), dispatch);
                    undo();
                }
                else {
                    dispatch({ type: 'CHARACTER_SAVE_ITEM', characterId: character.id, oldInstanceId: mod.instanceId, newInstanceId: assignModResponse.payload });
                }
            }
            catch(e) {
                undo();
                ToastDispatchables.toastLegacy(new ErrorToast("Server error adding and assigning body mod."), dispatch);
                console.error(e);
            }
        },

    removeMod: (character: CharacterInitializer, itemInstanceId: string, modInstanceId: string): AppThunkAction<CharacterItemAction | ToastAction> =>
        async (dispatch, getState) => {
            const itemResult = CharacterHelpers.searchItemByInstanceId(character, itemInstanceId);
            if (itemResult == null) {
                ToastDispatchables.toastLegacy(new ErrorToast("No item found to remove mod from."), dispatch);
                return;
            }
            const { reference: itemReference } = itemResult;

            const modReference = character.activeMods.find(m => m.instanceId == modInstanceId);
            if (modReference == null) {
                ToastDispatchables.toastLegacy(new ErrorToast("No mod found to remove."), dispatch);
                return;
            }

            const allItems = getState().library.items;
            const item = allItems.find(i => i.id == itemReference.id);
            if (item == null) {
                ToastDispatchables.toastLegacy(new ErrorToast("No backing item found."), dispatch);
                return;
            }

            const mod = allItems.find(i => i.id == modReference.id);
            if (mod == null) {
                ToastDispatchables.toastLegacy(new ErrorToast("No backing mod found."), dispatch);
                return;
            }

            const slotId = modReference.assignedSlotId;
            if (slotId == undefined) {
                console.error('Attempt to remove an unassigned mod.');
                return;
            }

            const undo = () => {
                dispatch({ type: 'CHARACTER_ASSIGN_MOD', characterId: character.id, itemInstanceId: itemReference.instanceId, modInstanceId: modReference.instanceId, slotId });
                ToastDispatchables.toastLegacy(new ErrorToast(`Error removing ${ItemHelpers.getName(mod, modReference)} from ${ItemHelpers.getName(item, itemReference)}.`), dispatch);
            }
            
            dispatch({ type: 'CHARACTER_REMOVE_MOD', characterId: character.id, itemInstanceId: itemReference.instanceId, slotId });

            try {
                const response = await ApiDefaultState.characters.removeMod(character.id, itemReference.instanceId, modReference.instanceId);
                if (response.status == 'Error') {
                    undo();
                    ToastDispatchables.toastValidationResults(response.validationResults, dispatch);
                }
            }
            catch (e){
                undo();
                ToastDispatchables.toastLegacy(new ErrorToast("Server error removing mod from item."), dispatch);
                console.error(e);
            }
        },

    removeBodyMod: (character: CharacterInitializer, reference: OwnedModReference): AppThunkAction<CharacterItemAction | ToastAction> =>
        async (dispatch, getState) => {
            const mod = getState().library.items.find(i => i.id == reference.id);
            if (mod == null) {
                ToastDispatchables.toastLegacy(new ErrorToast("No backing mod found."), dispatch);
                return;
            }

            const slotId = reference.assignedSlotId;
            if (slotId == undefined) {
                console.error("Attempt to remove an unassigned mod.");
                return;
            }

            const undo = () => {
                dispatch({ type: 'CHARACTER_ASSIGN_BODY_MOD', characterId: character.id, modInstanceId: reference.instanceId, slotId });
                ToastDispatchables.toastLegacy(new ErrorToast(`Error removing ${ItemHelpers.getName(mod, reference)} from body.`), dispatch);
            }
            
            dispatch({ type: 'CHARACTER_REMOVE_BODY_MOD', characterId: character.id, slotId });

            try {
                const response = await ApiDefaultState.characters.removeBodyMod(character.id, reference.instanceId);
                if (response.status == 'Error') {
                    undo();
                    ToastDispatchables.toastValidationResults(response.validationResults, dispatch);
                }
            }
            catch (e) {
                undo();
                ToastDispatchables.toastLegacy(new ErrorToast("Server error removing body mod."), dispatch);
                console.error(e);
            }
        },

    addSlots: (character: CharacterInitializer, requests: AddItemSlotsRequest[]): AppThunkAction<CharacterItemAction | ToastAction> =>
        async (dispatch, getState) => {
            for (let request of requests) {
                for (let slot of request.slots) {
                    slot.id = getUniqueIdentifier();
                }
            }
            const itemInstanceIds = requests.map(r => r.itemInstanceId)
            const slotIds = requests.mapMany(r => r.slots).map(s => s.id);

            const undo = () => {
                dispatch({ type: 'CHARACTER_REMOVE_SLOTS', characterId: character.id, itemInstanceIds, slotIds });
                ToastDispatchables.toastLegacy(new ErrorToast('Error updating item slots.'), dispatch);
            }

            dispatch({ type: 'CHARACTER_ADD_SLOTS', characterId: character.id, requests });

            try {
                const response = await ApiDefaultState.characters.addSlots(character.id, requests);
                if (response.status == 'Error') {
                    undo();
                    ToastDispatchables.toastValidationResults(response.validationResults, dispatch);
                }
                else {
                    dispatch({ type: 'CHARACTER_UPDATE_SLOTS', characterId: character.id, itemInstanceIds, updateIdResponses: response.payload });
                }
            }
            catch (e) {
                undo();
                ToastDispatchables.toastLegacy(new ErrorToast("Server error adding slots to item(s)."), dispatch);
                console.error(e);
            }
        },

    removeSlots: (character: CharacterInitializer, slots: ModSlot[]): AppThunkAction<CharacterItemAction | ToastAction> =>
        async (dispatch, getState) => {

            const slotIds = slots.map(s => s.id);
            const affectedItems = CharacterHelpers.getInventory(character).filter(i => i.customSlots.map(cs => cs.id).intersection(slotIds).any());
            const itemIds = affectedItems.map(ai => ai.instanceId);
            const undoModAssignments: CharacterAssignMod[] = [];
            const undoRequests: AddItemSlotsRequest[] = [];

            for (let item of affectedItems) {
                const mod = character.activeMods.find(m => slotIds.contains(m.assignedSlotId));
                if (mod?.assignedSlotId != null) {
                    undoModAssignments.push({ type: 'CHARACTER_ASSIGN_MOD', characterId: character.id, itemInstanceId: item.instanceId, modInstanceId: mod.instanceId, slotId: mod.assignedSlotId })
                }

                const slots = item.customSlots.filter(cs => slotIds.contains(cs.id));
                undoRequests.push({
                    itemInstanceId: item.instanceId,
                    slots
                });
            }

            const undo = () => {
                dispatch({ type: 'CHARACTER_ADD_SLOTS', characterId: character.id, requests: undoRequests });

                //If a mod is assigned, reassign it.
                for (let undoModAssignment of undoModAssignments) {
                    dispatch(undoModAssignment);
                }
            }

            dispatch({ type: 'CHARACTER_REMOVE_SLOTS', characterId: character.id, itemInstanceIds: itemIds, slotIds: slotIds });
            try {
                const response = await ApiDefaultState.characters.removeSlots(character.id, slotIds);
                if (response.status == 'Error') {
                    undo();
                    ToastDispatchables.toastLegacy(new ErrorToast('Error removing item slot.'), dispatch);
                }
            }
            catch (e) {
                undo();
                ToastDispatchables.toastLegacy(new ErrorToast("Server error removing slots from item(s)."), dispatch);
                console.error(e);
            }
        }
}

type MoveDispatcher = (action: CharacterMoveItems | ToastAction) => void;

/**
 * Creates a method that undoes an item move action.
 * @param character The character to create a move undo operation for.
 * @param reference The item that was moved.
 * @param replaceItems The items replaced in the move.
 * @param dispatch A hook into redux the dispatch pipeline.
 * @returns A function that undoes an item move action.
 */
const getMoveItemUndoFunction = (character: CharacterInitializer, reference: OwnedItemReference, replaceItems: OwnedItemReference[], dispatch: MoveDispatcher) => {
    const collection = CharacterHelpers.getItemCollection(character, reference);
    if (collection == undefined) {
        ToastDispatchables.toastLegacy(new ErrorToast('Move item not on character!'), dispatch);
        return;
    }

    //TODO: See if there is a way to keep this typing without repeating as much as is here.
    const itemPayload = { group: [reference], collection };

    const toDispatch: CharacterMoveItems = { type: 'CHARACTER_MOVE_ITEMS', characterId: character.id, operationOne: itemPayload }
    if (!replaceItems.any()) {
        return () => dispatch(toDispatch);
    }

    const replaceOperation = getMoveItemReplaceOperation(character, replaceItems, dispatch);
    return () => dispatch({ ...toDispatch, operationTwo: replaceOperation });
}

/**
 * Creates an operation for undoing the changes to items replaced in an item move action.
 * @param character The character to create a move undo operation for.
 * @param replaceItems The items replaced in the move.
 * @param dispatch A hook into redux the dispatch pipeline.
 * @returns A payload that reverses a move operation's changes to the replaced items.
 */
const getMoveItemReplaceOperation = (character: CharacterInitializer, replaceItems: OwnedItemReference[], dispatch: MoveDispatcher): MoveItemGroupOperation | undefined => {
    const replaceCollection = replaceItems.map(ri => ({
            item: ri,
            collection: CharacterHelpers.getItemCollection(character, ri)
        }))
        .filter(r => r.collection != null);

    const replaceGroupItems = replaceCollection.map(r => r.item);

    for (let response of replaceCollection) {
        if (replaceCollection.any(other => other.collection != response.collection)) {
            ToastDispatchables.toastLegacy(new ErrorToast('Replacement items are in different locations.'), dispatch);
            return;
        }
    }

    const collection = replaceCollection[0]?.collection;
    if (collection == null) {
        return undefined;
    }
    return { group: replaceGroupItems, collection };
}